import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Nullable } from 'src/types/nullable.type';

import { BubbleMenu } from '@tiptap/react';
import { NodeSelection } from 'prosemirror-state';

import { ChainedCommands, isTextSelection } from '@tiptap/core';
import { Editor } from '@tiptap/react';

import { DeleteStrongIcon, LeftStrongIcon, LinkStrongIcon } from 'src/monet/icons';
import isNull from 'lodash/isNull';
import { formatUrl } from 'src/utils/url';
import { isValidUrl } from 'src/utils/custom-validations';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { NeueBubbleMenuItem } from 'src/document-editor/components/neue-bubble-menu/item';
import { isCmdShiftZ, isCmdZ } from 'src/document-editor/neue-bubble-menu-fixed';

export const MutualActionPlanTextMenu = ({
  editor,
  showMenu,
  activeMenuView,
  onLinkClick,
  onLinkMenuClose,
}: {
  activeMenuView: Nullable<'link'>;
  editor: Nullable<Editor>;
  showMenu: boolean;
  onLinkMenuClose: () => void;
  onLinkClick: (selectedText: string) => void;
}) => {
  const [selectedText, setSelectedText] = useState('');
  const linkInputRef = useRef<Nullable<HTMLInputElement>>(null);
  const [menuViewPlaceholder, setMenuViewPlaceholder] = useState<Nullable<string>>(null);
  const { setErrorNotification } = useNotificationMessage();

  const shouldShow = (props: any) => {
    const { view, state, from, to } = props;
    const { doc, selection } = state;
    const { empty } = selection;

    const isNodeSelection = selection instanceof NodeSelection;
    const docSelectedText = doc.textBetween(from, to);

    if (docSelectedText !== selectedText) {
      setSelectedText(docSelectedText);
    }
    // Sometime check for `empty` is not enough.
    // Doubleclick an empty paragraph returns a node size of 2.
    // So we check also for an empty text size.
    const isEmptyTextBlock = !docSelectedText.length && isTextSelection(state.selection);

    const isWhitespaceBlock = selectedText ? selectedText.trim().length === 0 : false;

    if (!view.hasFocus() || empty || isEmptyTextBlock || isWhitespaceBlock || isNodeSelection) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!showMenu || isNull(activeMenuView)) {
      closeLinkMenu();
    }
  }, [showMenu, activeMenuView]);

  const runWithManagedSelection = (fn: (command: ChainedCommands, selection: any) => void) => {
    if (editor) {
      const command = editor.chain();
      command.focus();
      fn(command, editor.state.selection);
      command.run();
      setTimeout(() => {
        editor.view.dom.focus();
      }, 0);
    }
  };

  const openLinkMenu = () => {
    setMenuViewPlaceholder('Enter a url');
    if (editor) {
      setTimeout(() => {
        editor.view.dom.focus();
      }, 0);
    }
  };

  useEffect(() => {
    if (menuViewPlaceholder) {
      setTimeout(() => {
        if (linkInputRef.current) {
          linkInputRef.current.focus();
        }
      }, 300);
    }
  }, [menuViewPlaceholder]);

  if (!editor) {
    return null;
  }

  const buttonClasses = (selector: any, args = {}) => {
    return classNames('flex items-center justify-center w-5 h-5 rounded hover:text-white transition', {
      'text-white': editor.isActive(selector, args),
      'text-bedrock-dark-gray': !editor.isActive(selector, args),
    });
  };

  const closeLinkMenu = () => {
    if (editor && !editor.getAttributes('link').href) {
      editor.commands.unsetLink();
    }
    onLinkMenuClose();
  };

  const onMenuViewFormSubmit = (text: string) => {
    if (activeMenuView === 'link') {
      const url = text.trim();
      if (!url) {
        runWithManagedSelection((command) => {
          command.unsetLink();
        });
      } else {
        const formattedUrl = formatUrl(url);
        if (!isValidUrl(formattedUrl)) {
          setErrorNotification('Invalid URL');
          return;
        }
        editor?.commands.setLink({ href: formattedUrl });
        closeLinkMenu();
      }
    }
  };

  const onKeyDownCallback = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onMenuViewFormSubmit(e.currentTarget.value);
    } else if (e.key === 'Escape' || isCmdZ(e) || isCmdShiftZ(e)) {
      e.preventDefault();
      closeLinkMenu();
    }
  };

  const renderLinkInput = () => {
    return (
      <>
        <NeueBubbleMenuItem
          selected={false}
          onClick={() => {
            if (editor && !editor.getAttributes('link').href) {
              editor.commands.unsetLink();
            }
          }}
          tabIndex={0}
        >
          <LeftStrongIcon onClick={onLinkMenuClose} className='w-4 h-4' />
        </NeueBubbleMenuItem>
        <input
          ref={linkInputRef}
          type='text'
          autoFocus
          tabIndex={0}
          className={classNames(
            'w-[270px] py-[3px] px-[13px] rounded text-bedrock-p text-white bg-neue-green-background border-2 border-neue-green-primary',
            'focus:ring-0 outline-none focus:border-neue-green-primary focus-visible:border-neue-green-primary focus:outline-none active:border-neue-green-primary hover:border-neue-green-primary'
          )}
          placeholder={menuViewPlaceholder || ''}
          defaultValue={editor.getAttributes('link').href}
          onKeyDown={onKeyDownCallback}
        />
        <NeueBubbleMenuItem
          selected={false}
          onClick={() => {
            if (editor) {
              editor.commands.unsetLink();
            }
          }}
          disabled={!editor.isActive('link')}
          tabIndex={0}
        >
          <DeleteStrongIcon className='w-4 h-4' />
        </NeueBubbleMenuItem>
      </>
    );
  };

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={shouldShow}
      tippyOptions={{
        placement: 'top-start',
        popperOptions: { strategy: 'fixed' },
        onHide: onLinkMenuClose,
      }}
    >
      <div className='flex flex-row p-2 bg-bedrock-black rounded-lg'>
        {activeMenuView === 'link' ? (
          renderLinkInput()
        ) : (
          <div className='flex divide-x divide-bedrock-white/10'>
            <button
              type='button'
              onClick={() => {
                openLinkMenu();
                onLinkClick(selectedText);
              }}
              className={buttonClasses('link')}
            >
              <LinkStrongIcon />
            </button>
          </div>
        )}
      </div>
    </BubbleMenu>
  );
};
